import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useChallenges } from '../hooks/useChallenges'
import { useWallet } from '../contexts/WalletContext'
import { resolveGradient, resolveEmoji } from '../lib/challengeTheme'
import {
  getChallengeStatus,
  getChallengeStatusLabel,
  getChallengeTypeName,
  formatWeiToEth,
  formatTimestamp,
  getRemainingText,
  CHALLENGE_TYPE,
  type ChallengeListItem,
  type ChallengeStatus,
  type FilterTab,
  type TypeFilter,
} from '../types/challenge'

// ── Badge helpers ─────────────────────────────────────────────────────────────

function statusBadgeClass(status: ChallengeStatus): string {
  switch (status) {
    case 'active':
      return 'badge badge-success'
    case 'upcoming':
      return 'badge badge-neutral'
    case 'ended':
      return 'badge badge-ink'
    case 'refunded':
      return 'badge badge-warning'
  }
}

function typeBadgeClass(type: number): string {
  return type === CHALLENGE_TYPE.ENTRY_FEE ? 'badge badge-primary' : 'badge badge-warning'
}

// ── Thumb theme: creator-chosen (off-chain) with index-based fallback ─────────

function ChallengeCard({
  challenge,
  idx,
  onJoin,
}: {
  challenge: ChallengeListItem
  idx: number
  onJoin: (address: string, entryFee: bigint) => void
}) {
  const status = getChallengeStatus(challenge)
  const isEnded = status === 'ended' || status === 'refunded'
  // Joining is only possible while the challenge is still PENDING (upcoming).
  // Once it's ACTIVE ('진행 중') the on-chain join() reverts, so we must not show a join button.
  const canJoin = status === 'upcoming'
  const pct =
    challenge.maxParticipants > 0
      ? Math.min(100, Math.round((challenge.participantCount / challenge.maxParticipants) * 100))
      : 0

  function handleJoinClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    onJoin(challenge.address, challenge.entryFee)
  }

  return (
    <Link to={`/challenge/${challenge.address}`} className="ccard" data-testid="challenge-card">
      {/* Thumbnail */}
      <div className="ccard-thumb" style={{ background: resolveGradient(challenge.color, idx) }}>
        <div className="ccard-thumb-inner">
          <span style={{ opacity: 0.85 }}>{resolveEmoji(challenge.emoji, idx)}</span>
        </div>
        <div className="ccard-badges">
          <span className={statusBadgeClass(status)}>
            <span className="dot" style={{ background: 'currentColor', opacity: 0.7 }} />
            {getChallengeStatusLabel(status)}
          </span>
          <span className={typeBadgeClass(challenge.challengeType)}>
            {getChallengeTypeName(challenge.challengeType)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="ccard-body">
        <p className="ccard-name">{challenge.name}</p>
        <div className="ccard-meta">
          {challenge.challengeType === CHALLENGE_TYPE.ENTRY_FEE ? (
            <div className="ccard-row">
              <span className="k">참가비</span>
              <span className="v eth-amt">{formatWeiToEth(challenge.entryFee)} ETH</span>
            </div>
          ) : (
            <div className="ccard-row">
              <span className="k">경품</span>
              <span className="v eth-amt">{formatWeiToEth(challenge.entryFee)} ETH</span>
            </div>
          )}
          <div className="ccard-row">
            <span className="k">참가자</span>
            <span className="v">
              {challenge.participantCount} / {challenge.maxParticipants}명
            </span>
          </div>
          <div className="meter" style={{ marginTop: 2 }}>
            <i style={{ width: `${pct}%` }} />
          </div>
          <div className="ccard-row">
            <span className="k">기간</span>
            <span className="v" style={{ fontSize: 12 }}>
              {formatTimestamp(challenge.startTime)} ~ {formatTimestamp(challenge.endTime)}
            </span>
          </div>
        </div>
        <div className="ccard-foot">
          <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
            {getRemainingText(challenge.endTime)}
          </span>
          {canJoin ? (
            <button className="btn btn-primary btn-sm" onClick={handleJoinClick}>
              참가하기
            </button>
          ) : (
            <span className="btn btn-subtle btn-sm">{isEnded ? '결과보기' : '상세 보기'}</span>
          )}
        </div>
      </div>
    </Link>
  )
}

// ── Filters ───────────────────────────────────────────────────────────────────

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'active', label: '진행 중' },
  { key: 'upcoming', label: '시작 예정' },
  { key: 'ended', label: '종료됨' },
]

function matchesTab(c: ChallengeListItem, tab: FilterTab) {
  if (tab === 'all') return true
  const s = getChallengeStatus(c)
  if (tab === 'ended') return s === 'ended' || s === 'refunded'
  return s === tab
}
function matchesType(c: ChallengeListItem, type: TypeFilter) {
  if (type === 'all') return true
  return c.challengeType === Number(type)
}
function matchesSearch(c: ChallengeListItem, q: string) {
  if (!q.trim()) return true
  return c.name.toLowerCase().includes(q.toLowerCase())
}

function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--ink-4)"
      strokeWidth="2.2"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="22" y2="22" />
    </svg>
  )
}

// ── Stats computation ─────────────────────────────────────────────────────────

function computeStats(challenges: ChallengeListItem[]) {
  const active = challenges.filter((c) => getChallengeStatus(c) === 'active').length
  const totalPool = challenges.reduce((acc, c) => {
    if (c.challengeType === CHALLENGE_TYPE.ENTRY_FEE) {
      return acc + c.entryFee * BigInt(c.participantCount)
    }
    return acc
  }, 0n)
  return { active, totalPool }
}

// ── Home (SCR-001) ────────────────────────────────────────────────────────────

export default function Home() {
  const { data: challenges, isLoading, isError } = useChallenges()
  const { isConnected, openConnectModal } = useWallet()
  const navigate = useNavigate()

  const [tab, setTab] = useState<FilterTab>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [search, setSearch] = useState('')

  function handleJoin(address: string, entryFee: bigint) {
    if (!isConnected) {
      openConnectModal()
      return
    }
    void navigate(`/challenge/${address}`)
    void entryFee
  }

  const all = challenges ?? []
  const { active, totalPool } = computeStats(all)
  const filtered = all.filter(
    (c) => matchesTab(c, tab) && matchesType(c, typeFilter) && matchesSearch(c, search),
  )

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="hero-eyebrow">
          <span>⛓</span> Ethereum Sepolia
        </div>
        <h1 className="hero-title">완주하면 나눠 갖습니다.</h1>
        <p className="hero-sub">
          중개자 없는 도전 플랫폼. 참가비를 모아 완주자끼리 균등 분배하고, 모든 정산은 스마트
          컨트랙트가 자동으로 처리합니다. 기록·탈락·정산이 전부 온체인에 투명하게 공개됩니다.
        </p>
        <div className="hero-actions">
          <Link to="/create" className="btn btn-primary btn-lg">
            챌린지 개설하기
          </Link>
        </div>
      </section>

      {/* Stats */}
      {!isLoading && !isError && all.length > 0 && (
        <div className="statrow" style={{ marginBottom: 32 }}>
          <div className="stat">
            <div className="lbl">진행 중 챌린지</div>
            <div className="val">
              {active}
              <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 3 }}>개</span>
            </div>
          </div>
          <div className="stat">
            <div className="lbl">누적 참가비 풀</div>
            <div className="val eth-amt">{formatWeiToEth(totalPool)} ETH</div>
          </div>
          <div className="stat">
            <div className="lbl">전체 챌린지</div>
            <div className="val">
              {all.length}
              <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 3 }}>개</span>
            </div>
          </div>
        </div>
      )}

      {/* Section heading */}
      <div className="section-hd" id="challenges">
        <h2 className="section-title">챌린지 목록</h2>
      </div>

      {/* Filter bar */}
      <div className="filterbar">
        <div className="segtabs" role="tablist">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              className={`segtab${tab === key ? ' active' : ''}`}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={tab === key}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          className="select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          aria-label="챌린지 타입 필터"
        >
          <option value="all">전체 타입</option>
          <option value="0">참가비</option>
          <option value="1">홍보-ETH</option>
          <option value="2">홍보-실물</option>
        </select>
        <div className="searchbox">
          <SearchIcon />
          <input
            type="search"
            placeholder="챌린지 이름 검색…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="챌린지 이름 검색"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="empty">
          <div className="spinner spinner-lg" />
          <p>챌린지 목록을 불러오는 중…</p>
        </div>
      ) : isError ? (
        <div className="empty">
          <span className="empty-icon">⚠️</span>
          <p>데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <span className="empty-icon">🔍</span>
          <p>
            {all.length === 0 ? '아직 개설된 챌린지가 없습니다.' : '조건에 맞는 챌린지가 없습니다.'}
          </p>
        </div>
      ) : (
        <div className="cgrid">
          {filtered.map((c, i) => (
            <ChallengeCard key={c.address} challenge={c} idx={i} onJoin={handleJoin} />
          ))}
        </div>
      )}

      {/* FAB */}
      <Link to="/create" className="fab" aria-label="챌린지 개설">
        <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
        챌린지 개설
      </Link>
    </>
  )
}
